import { useLocation } from 'react-router-dom';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks';
import bgImage from '../../assets/images/bg.png';
import * as cts from '../../constants/ui/svg';
import 'antd/dist/antd.css';
import { useFullscreenSafe } from './components/useFullScreenSafe';
import NavMenu from '../../components/Common/NavMenu';
import { rankingAction } from 'slices/rankingSlice';
import { shopSettingActions } from 'slices/shopSettingSlice';
import { getToken } from 'helpers';
import moment from 'moment';
import { LoadingOutlined } from '@ant-design/icons';
import { changeArr } from '../../../src/helpers/functionHelper';
import { DisplayScreenType } from 'models';
import { Spin } from 'antd';
import { shopListAction } from 'slices/shopSlice';
import LayoutRow from './components/layoutRow';
import LayoutSlide from './components/layoutSlide';

const HomePage = () => {
  const { pathname } = useLocation();
  const { enterFullscreen, exitFullscreen } = useFullscreenSafe();
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const token = getToken() as string;

  const isChrome =
    typeof window !== 'undefined' &&
    'chrome' in window &&
    (!!(window.chrome as any)?.webstore || !!(window.chrome as any)?.runtime);
  const userAgent = window.navigator.userAgent;
  const isAndroid = /Android/.test(userAgent);
  const isIOS = /iPhone|iPad|iPod/.test(userAgent);
  const fullscreenOnReload =
    typeof window !== 'undefined' ? (localStorage.getItem('fullscreenOnReload') ?? 'true') : 'true';

  const shopSetting = useAppSelector((state) => state.shopSetting.shopSetting);
  const selectShop = useAppSelector((state) => state.shopList.selectShop);
  const loading = useAppSelector((state) => state.ranking.loading);
  const rankingNearestPublic = useAppSelector((state) => state.ranking.rankingNearestPublic);
  const rankingMonthPublic = useAppSelector((state) => state.ranking.rankingMonthPublic);
  const rankingGroupShopPublic = useAppSelector((state) => state.ranking.rankingPublicGroupShop);
  const rankingGroupShopMonthPublic = useAppSelector(
    (state) => state.ranking.rankingPublicMonthGroupShop
  );
  const orderByImagesSliderRank: Array<any> = useAppSelector(
    (state) => state.ranking.orderByImagesSliderRank
  );
  const shopList = useAppSelector((state) => state.shopList.listShop);
  const orderByRankingInPosRanking = useAppSelector(
    (state) => state.ranking.orderByRankingInPosRanking
  );
  const dayHaveData = useAppSelector((state) => state.ranking.dayHaveData);
  const programRank = useAppSelector((state) => state.ranking.programRank);
  const masterInfo = useAppSelector((state) => state.auth.masterInfo);

  const [openMenu, setOpenMenu] = useState(false);
  const [selectMenu, setSelectMenu] = useState(1);
  const [currentRankingItemRunning, setCurrentRankingItemRunning] = useState<number>(0);
  const [currentTypeDisplayOnScreen, setCurrentTypeDisplayOnScreen] =
    useState<DisplayScreenType>('');
  const [arrLoop, setArrLoop] = useState<any[]>([]);
  const [arrNearestLoop, setArrNearestLoop] = useState<any[]>([]);
  const [currentDate, setCurrentDate] = useState(moment().startOf('month'));
  const [displayData, setDisplayData] = useState<{
    object: any;
    secondChange: number;
  } | null>(null);
  const [displayDayHaveNearestData, setDisplayDayHaveNearestData] = useState<{
    object: any;
    secondChange: number;
  } | null>(null);

  const isMaster = masterInfo.active && !selectShop.id;
  const limitReqGetDataRankNearest = useRef(0);

  // const checkShopStardamGroup = useMemo(
  //   () => (selectShop as any).group_shop === 1,
  //   [selectShop.id]
  // );

  const shopIds = useMemo(() => {
    const extractShopId = (item: any): number | undefined => {
      // current shop has id in item ranking of program
      if (item?.shop_id !== null) return item.shop_id;

      // others shop have id in item ranking of program
      if (item?.select_shop_id != null) return item.select_shop_id;

      // program ranking → use select_shop_id
      if (item.order_by_image_slider_ranking_id) {
        const ranking = item.order_by_images_slider_ranking;
        if (ranking?.ranking_list_shop)
          return ranking.ranking_list_shop?.flatMap((item: any) => item?.select_shop_id);
        if (ranking?.select_shop_id) return ranking.select_shop_id;
      }

      if (item.order_by_ranking_id) {
        if (item?.ranking_list_shop)
          return item.ranking_list_shop?.flatMap((item: any) => item?.select_shop_id);
        if (item?.select_shop_id) return item.select_shop_id;
      }

      // default return select shop => current shop
      return selectShop.id;
    };

    const ids = orderByImagesSliderRank?.flatMap(extractShopId)?.filter(Boolean) ?? [];
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set
    return Array.from(new Set(ids));
  }, [orderByImagesSliderRank, selectShop?.id]);

  const shopIdGroupRanking = useMemo(() => {
    const filteredGroupShopRanking = orderByImagesSliderRank.filter((item: any) => {
      if (isMaster) {
        if (item.order_by_ranking_id) {
          return item.ranking_list_shop.length > 0;
        }
      }
      if (item.order_by_image_slider_ranking_id) {
        if (item.order_by_images_slider_ranking.ranking_list_shop) {
          return item.order_by_images_slider_ranking.ranking_list_shop.length > 0;
        }
      }
    });
    if (filteredGroupShopRanking.length == 0) return [];
    if (isMaster) {
      return Array.from(
        new Set(
          filteredGroupShopRanking
            ?.flatMap((item: any) => item.ranking_list_shop)
            ?.map((item: any) => item.select_shop_id)
            ?.filter(Boolean)
        )
      );
    }
    return Array.from(
      new Set(
        filteredGroupShopRanking
          ?.flatMap((item: any) => item.order_by_images_slider_ranking?.ranking_list_shop)
          ?.map((item: any) => item.select_shop_id)
          ?.filter(Boolean)
      )
    );
  }, [orderByImagesSliderRank, isMaster]);

  const defaultSecondChange = useMemo(() => {
    return shopSetting?.data?.second_change_ranking * 1000 || 30000;
  }, [shopSetting?.data]);

  const getDateHaveDataRanking = useCallback(() => {
    if (!shopIds?.length) return;
    if (displayData?.object.length > 0) return;
    shopIds.forEach((shopId) => {
      dispatch(
        rankingAction.getDateHaveDataRanking({
          token,
          params: {
            shop_id: String(shopId),
          },
        })
      );
    });
  }, [dispatch, token, shopIds]);

  const transformedArr = useMemo(() => {
    const hasShopId = orderByRankingInPosRanking.some(
      (item: any) => item.shop_id === selectShop?.id
    );
    if (hasShopId) {
      return changeArr(orderByRankingInPosRanking);
    } else {
      return [];
    }
  }, [orderByRankingInPosRanking, selectShop?.id]);

  const getDataByName = (name?: string) =>
    name ? transformedArr.find((x) => x.name === name)?.data || [] : [];

  const sourceNameRefactor =
    displayData !== null
      ? displayData.object?.[currentRankingItemRunning]
      : displayDayHaveNearestData !== null
        ? displayDayHaveNearestData.object?.[currentRankingItemRunning]
        : 0;
  // if current item has link, which mean is media, set to layout 0
  const layout = sourceNameRefactor?.link ? 0 : (sourceNameRefactor?.layout_ranking ?? 0);

  const currentData = displayData || displayDayHaveNearestData;

  const checkShopStardamGroup = useMemo(() => {
    const currentShopId =
      currentData?.object?.[currentRankingItemRunning]?.infoItem?.select_shop_id ?? selectShop?.id;

    if (isMaster) {
      if (currentData?.object?.[currentRankingItemRunning]?.infoItem?.ranking_list_shop?.length > 0)
        return true;
    } else {
      if (!currentShopId) return false;
    }

    const findShopInfoByCurrentShopId = shopList.find((item) => item.id === currentShopId);

    return findShopInfoByCurrentShopId?.group_shop === 1;
  }, [currentRankingItemRunning, currentData?.object, shopList, selectShop?.id]);

  const hasDisplayData =
    !!displayData?.object?.length || !!displayDayHaveNearestData?.object?.length;

  const isRanking =
    currentTypeDisplayOnScreen === 'ranking-day' || currentTypeDisplayOnScreen === 'ranking-month';

  const renderRankingTitleText = () => {
    const isProfit = sourceNameRefactor?.infoItem?.name?.includes('PROFIT');
    const currentDataRunning = currentData?.object[currentRankingItemRunning];
    if (!currentDataRunning?.type || !currentDataRunning?.date) return '';
    const dateText =
      currentDataRunning.type === 'date'
        ? moment(currentDataRunning.date).format('M月D日')
        : moment(currentDataRunning.date).format('M月');

    return dateText + (isProfit ? '人気のあるキャスト' : '頑張っているキャスト');
  };

  const transformedArrSorted = [...orderByImagesSliderRank]
    .sort((a: any, b: any) => {
      const aItem = a.order_by == null;
      const bItem = b.order_by == null;

      if (aItem && !bItem) return -1;
      if (!aItem && bItem) return 1;

      if (aItem && bItem) {
        return (
          (a.order_by_images_slider_ranking?.order_by ?? 0) -
          (b.order_by_images_slider_ranking?.order_by ?? 0)
        );
      }

      return (a.order_by ?? 0) - (b.order_by ?? 0);
    })
    .map((item: any) => {
      // master program or master standby image and vid or group shop ranking
      if (item?.order_by_image_slider_ranking_id) {
        const sliderRanking = item?.order_by_images_slider_ranking;

        if (!sliderRanking) return item;

        // master program
        if (sliderRanking.order_by_ranking) {
          const name = sliderRanking.order_by_ranking.name;

          return {
            ...item,
            name,
            shop_id: sliderRanking.select_shop_id ?? item.shop_id,
            data: getDataByName(name),
            second_change: sliderRanking.second_change,
          };
        }

        // master standby image / video
        if (sliderRanking.images_slider) {
          return {
            ...item,
            name: `IMAGE_SLIDER_${sliderRanking.id}`,
            data: [sliderRanking.images_slider],
            second_change: sliderRanking.second_change,
          };
        }

        return item;
      }

      // normal program
      if (item?.order_by_ranking) {
        const name = item.order_by_ranking.name;

        return {
          ...item,
          shop_id: item.select_shop_id ?? item.shop_id,
          name,
          data: getDataByName(name),
          second_change: item.second_change,
        };
      }

      if (item?.images_slider) {
        return {
          ...item,
          name: `IMAGE_SLIDER_${item.images_slider.id}`,
          data: [item.images_slider],
          second_change: item.second_change,
        };
      }

      return item;
    })
    .filter(Boolean);

  // only get the first day which have data to fetch and set into dataNearestArr in localStorage by limitReqGetDataRankNearest.current,
  const handleGetDataNearestDay = useCallback(() => {
    // if wanna get more data at first time, increase limit below this comment line

    if (limitReqGetDataRankNearest.current >= 1) return;
    if (!dayHaveData.length || !shopIds.length) return;
    const currentIndex = limitReqGetDataRankNearest.current;
    dayHaveData.forEach(
      (shopData: { dates: Array<{ date: string; count: number }>; shop_id: number }) => {
        const dateItem = shopData.dates?.[currentIndex];
        if (!dateItem) return;

        const currentDay = moment(dateItem.date, 'YYYYMMDD').format('YYYY-MM-DD');
        dispatch(
          rankingAction.getRankingNearestDayPublic({
            token,
            params: {
              shop_id: shopData.shop_id,
              type: 'date',
              date: currentDay,
              show_rank: 1,
              key: '@Aube2021',
            },
          })
        );
      }
    );

    limitReqGetDataRankNearest.current++;
  }, [dayHaveData, dispatch, token, shopIds]);

  const fetchArrLoop = useCallback(async () => {
    const localDateRank = JSON.parse(localStorage.getItem('dataDateRank') || '[]');
    const localNearestData = JSON.parse(localStorage.getItem('dataDateRankNearest') || '[]');
    const localMonthRank = JSON.parse(localStorage.getItem('dataMonthRank') || '[]');
    const localGroupShopRanking = JSON.parse(localStorage.getItem('dataDateRankGroupShop') || '[]');
    const localGroupShopMonthRanking = JSON.parse(
      localStorage.getItem('dataMonthRankGroupShop') || '[]'
    );
    const dateSource =
      Array.isArray(localDateRank) && localDateRank.length ? localDateRank : rankingNearestPublic;
    const monthSource =
      Array.isArray(localMonthRank) && localMonthRank.length ? localMonthRank : rankingMonthPublic;
    const groupShopDatePublic =
      Array.isArray(localGroupShopRanking) && localGroupShopRanking.length
        ? localGroupShopRanking
        : rankingGroupShopPublic;
    const groupShopMonthPublic =
      Array.isArray(localGroupShopMonthRanking) && localGroupShopMonthRanking.length
        ? localGroupShopMonthRanking
        : rankingGroupShopMonthPublic;
    const today = moment().date();
    const isDayReady =
      dateSource.length > 0 && dateSource.length === today * Number(shopIds.length);
    const isMonthReady = monthSource?.length > 0;
    // kiểm tra đã đủ dữ liệu chưa, nếu chưa return
    if (!isDayReady || !isMonthReady) {
      // console.log('Data is not ready to display from start of month to current day');
      handleGetDataNearestDay();
      return;
    }

    const updatedArr = transformedArrSorted
      .map((item: any) => {
        let sortedData = [];

        if (item.name?.includes('DAY')) {
          if (item.shop_id) {
            sortedData = [...dateSource].sort((a, b) => {
              return moment(a?.date, 'MM月DD日').diff(moment(b?.date, 'MM月DD日'));
            });
          }
          if (isMaster) {
            if (item.order_by_ranking_id) {
              if (item?.ranking_list_shop?.length > 0) {
                sortedData = [...groupShopDatePublic].sort((a, b) => {
                  return moment(a?.date, 'MM月DD日').diff(moment(b?.date, 'MM月DD日'));
                });
              } else if (item?.select_shop_id) {
                sortedData = [...dateSource].sort((a, b) => {
                  return moment(a?.date, 'MM月DD日').diff(moment(b?.date, 'MM月DD日'));
                });
              }
            }
          } else {
            if (item.order_by_image_slider_ranking_id) {
              if (item?.order_by_images_slider_ranking?.ranking_list_shop?.length > 0) {
                sortedData = [...groupShopDatePublic].sort((a, b) => {
                  return moment(a?.date, 'MM月DD日').diff(moment(b?.date, 'MM月DD日'));
                });
              } else if (item?.order_by_images_slider_ranking?.select_shop_id) {
                sortedData = [...dateSource].sort((a, b) => {
                  return moment(a?.date, 'MM月DD日').diff(moment(b?.date, 'MM月DD日'));
                });
              }
            }
          }
        } else if (item.name.includes('MONTH')) {
          if (item.shop_id) {
            sortedData = Array.isArray(monthSource) ? [...monthSource] : [];
          }
          if (isMaster) {
            if (item.order_by_ranking_id) {
              if (item?.ranking_list_shop?.length > 0) {
                sortedData = Array.isArray(groupShopMonthPublic) ? [...groupShopMonthPublic] : [];
              } else if (item?.select_shop_id) {
                sortedData = Array.isArray(monthSource) ? [...monthSource] : [];
              }
            }
          } else {
            if (item.order_by_image_slider_ranking_id) {
              if (item?.order_by_images_slider_ranking?.ranking_list_shop?.length > 0) {
                sortedData = Array.isArray(groupShopMonthPublic) ? [...groupShopMonthPublic] : [];
              } else if (item?.order_by_images_slider_ranking?.select_shop_id) {
                sortedData = Array.isArray(monthSource) ? [...monthSource] : [];
              }
            }
          }
        } else {
          sortedData = item.data || [];
        }
        // if (sortedData.length > 0 && item.name.includes('DAY')) {
        if (sortedData.length > 0) {
          sortedData = sortedData.map((entry: any) => {
            const normalizedRelatedItems = Array.isArray(entry.relatedItems)
              ? entry.relatedItems
              : [];
            if (normalizedRelatedItems.length === 0) return [];
            return {
              ...entry,
              relatedItems: [...normalizedRelatedItems].sort((a, b) => {
                if (item.name?.includes('PROFIT')) {
                  return (a.rank || 0) - (b.rank || 0);
                }
                if (item.name?.includes('NOMINATION')) {
                  return (Number(a.appointee_times) || 0) - (Number(b.appointee_times) || 0);
                }
                return 0;
              }),
            };
          });
        }
        return {
          ...item,
          data: sortedData,
        };
      })
      .filter(Boolean);

    setArrLoop((prevArr) =>
      JSON.stringify(prevArr) === JSON.stringify(updatedArr) ? prevArr : updatedArr
    );

    if (localNearestData.length > 0) {
      setArrNearestLoop([]);
      setDisplayDayHaveNearestData(null);
      localStorage.removeItem('dataDateRankNearest');
      localStorage.setItem('fullscreenOnReload', 'true');
    }
  }, [
    transformedArrSorted,
    rankingMonthPublic,
    shopIds,
    rankingGroupShopPublic.length,
    rankingGroupShopMonthPublic.length,
  ]);

  const buildFlatData = (dataArray: any[]) => {
    return dataArray
      .map((item) => {
        if (item.name?.includes('IMAGE_SLIDER')) return item;
        return {
          ...item,
          data: item.data?.filter(
            (d: any) =>
              d.relatedItems?.length > 0 && (d.shop_id == null || d.shop_id === item.shop_id)
          ),
        };
      })
      .filter((item) => Array.isArray(item.data) && item.data.length > 0)
      .flatMap((item) => {
        return item.data.map((subItem: any) => {
          const isImageSliderRanking = !!item.order_by_image_slider_ranking_id;

          // info item is type of ranking like PROFIT_DAY, NOMINATION_DAY, PROFIT_MONTH, NOMINATION_MONTH
          const infoItem = item.order_by_ranking_id
            ? { ...item.order_by_ranking, ...item }
            : item.order_by_image_slider_ranking_id
              ? item.order_by_images_slider_ranking
              : item.images_slider;

          let result = {
            ...item,
            secondChange: item.second_change,
            subItem: {
              ...subItem,
              secondChange: item?.second_change,
              infoItem,
              layout_ranking: isImageSliderRanking
                ? (item.order_by_images_slider_ranking?.layout_ranking ?? 0)
                : (item.layout_ranking ?? 0),
            },
          };
          // get shop_id for single shop
          if (item.shop_id) {
            result = {
              ...result,
              subItem: {
                ...result.subItem,
                shop_id: Number(item.shop_id),
              },
            };
          }
          // if isMaster mode, filter data by group shop by get shop_ids in subItem.ranking_list_shop
          if (isMaster) {
            // get list shop for group shop by shop_ids
            if (item.ranking_list_shop?.length > 0) {
              result = {
                ...result,
                subItem: {
                  ...result.subItem,
                  // map data by group shop with ranking_list_shop in subItem
                  relatedItems: subItem.relatedItems?.filter((relatedItem: any) => {
                    if (item?.ranking_list_shop.length > 0) {
                      return item?.ranking_list_shop?.some(
                        (shop: any) => shop.select_shop_id === relatedItem.shop.id
                      );
                    }
                  }),
                  shop_ids: item.ranking_list_shop.map((item: any) => Number(item.select_shop_id)),
                },
              };
            }
          } else {
            // if not isMaster mode, filter data by group shop by get shop_ids in subItem.order_by_images_slider_ranking.ranking_list_shop
            // get list shop for group shop by shop_ids
            if (
              item?.order_by_image_slider_ranking_id &&
              item?.order_by_images_slider_ranking?.ranking_list_shop?.length > 0
            ) {
              result = {
                ...result,
                subItem: {
                  ...result.subItem,
                  // map data by group shop with ranking_list_shop in subItem
                  relatedItems: subItem.relatedItems?.filter((relatedItem: any) => {
                    if (item?.order_by_images_slider_ranking?.ranking_list_shop.length > 0) {
                      return item?.order_by_images_slider_ranking?.ranking_list_shop?.some(
                        (shop: any) => shop.select_shop_id === relatedItem.shop.id
                      );
                    }
                  }),
                  shop_ids: item?.order_by_images_slider_ranking.ranking_list_shop.map(
                    (item: any) => Number(item.select_shop_id)
                  ),
                },
              };
            }
          }
          return result;
        });
      })
      .filter((item) => {
        if (item.name?.includes('IMAGE_SLIDER')) return true;
        return item.subItem?.relatedItems?.length > 0;
      });
  };

  const processSequentially = useCallback(
    (dataArray: any[], runningIndex: number) => {
      const flatArr = buildFlatData(dataArray);
      if (!flatArr.length || !flatArr[runningIndex]) return;

      const { name, secondChange, second_change } = flatArr[runningIndex];
      // // IMAGE SLIDER
      // if (name?.includes('IMAGE_SLIDER')) {
      //   setDisplayData({
      //     object: flatArr.map((x) => x.subItem),
      //     secondChange: second_change || secondChange,
      //   });
      //   return;
      // }

      // // layout_ranking === 1
      // if (layout === 1) {
      //   setDisplayData({
      //     object: flatArr.map((x) => x.subItem),
      //     secondChange: second_change || secondChange,
      //   });
      //   return;
      // }

      // others layout (included layout !== 1 and standby (images + videos))
      setDisplayData({
        object: flatArr.map((x) => x.subItem),
        secondChange: second_change || secondChange,
      });
    },
    [layout]
  );

  const processSequentiallyNearest = useCallback(
    (dataArray: any[], runningIndex: number) => {
      const flatArr = buildFlatData(dataArray);

      if (!flatArr.length || !flatArr[runningIndex]) return;
      const { name, secondChange, second_change } = flatArr[runningIndex];

      // if (flatArr.length < orderByImagesSliderRank.length) return;
      // khi 1 vong chay xong roi moi set lai data, tranh viec set data lien tuc o master group shop
      if (runningIndex !== 0) return;
      // if (name?.includes('IMAGE_SLIDER')) {
      //   setDisplayDayHaveNearestData({
      //     object: flatArr.map((x) => x.subItem),
      //     secondChange: second_change || secondChange,
      //   });
      //   return;
      // }
      // if (layout === 1) {
      //   setDisplayDayHaveNearestData({
      //     object: flatArr.map((x) => x.subItem),
      //     secondChange: second_change || secondChange,
      //   });
      //   return;
      // }
      setDisplayDayHaveNearestData({
        object: flatArr.map((x) => x.subItem),
        secondChange: second_change || secondChange,
      });
    },
    [
      layout,
      arrNearestLoop,
      currentRankingItemRunning,
      orderByImagesSliderRank,
      displayDayHaveNearestData,
    ]
  );

  const fetchRankingPublic = useCallback(() => {
    const todayDate = currentDate.format('YYYY-MM-DD');
    const jsonData = JSON.parse(localStorage.getItem('dataDateRank') || '[]');
    // if (Array.isArray(jsonData)) {
    //   setArrRankingDate(jsonData);
    // }

    shopIds.forEach((shopId) => {
      if (!shopId) return;

      const existedDateData = jsonData?.some(
        (item: any) => item.shop_id === shopId && item.date === todayDate
      );

      if (!existedDateData) {
        dispatch(
          rankingAction.getRankingPublic({
            token,
            params: {
              shop_id: shopId,
              type: 'date',
              date: todayDate,
              show_rank: 1,
              key: '@Aube2021',
            },
          })
        );
      }
    });

    const jsonDataMonth = JSON.parse(localStorage.getItem('dataMonthRank') || '[]');

    shopIds.forEach((shopId) => {
      if (!shopId) return;

      const existedMonthData = jsonDataMonth?.some((item: any) => item.shop_id === shopId);
      if (!existedMonthData) {
        dispatch(
          rankingAction.getRankingMonthPublic({
            token,
            params: {
              shop_id: shopId,
              type: 'month',
              date: todayDate,
              show_rank: 1,
              key: '@Aube2021',
            },
          })
        );
      }
    });
  }, [dispatch, token, currentDate, shopIds]);

  const fetchRankingPublicGroupShop = useCallback(() => {
    // const todayDate = '2026-03-29';
    const todayDate = currentDate.format('YYYY-MM-DD');
    const jsonData = JSON.parse(localStorage.getItem('dataDateRankGroupShop') || '[]');
    const jsonDataMonth = JSON.parse(localStorage.getItem('dataMonthRankGroupShop') || '[]');
    if (shopIdGroupRanking.length > 0) {
      const existedDateData = jsonData?.some((item: any) => item.date === todayDate);

      if (!existedDateData) {
        dispatch(
          rankingAction.getRankingPublicGroupShop({
            token,
            params: {
              shop_ids: shopIdGroupRanking,
              type: 'date',
              date: todayDate,
              show_rank: 1,
              key: '@Aube2021',
            },
          })
        );
      }

      const shopSet = new Set(shopIdGroupRanking);
      const existedMonthData = jsonDataMonth?.some((item: any) =>
        item.shop_ids?.some((id: number) => shopSet.has(id))
      );
      if (!existedMonthData) {
        dispatch(
          rankingAction.getRankingPublicGroupShopMonth({
            token,
            params: {
              shop_ids: shopIdGroupRanking,
              type: 'month',
              date: todayDate,
              show_rank: 1,
              key: '@Aube2021',
            },
          })
        );
      }
    }
  }, [dispatch, token, shopIdGroupRanking.length, currentDate]);

  const getListNoteAppRanking = useCallback(() => {
    if (!programRank?.id || !shopIds.length) return;

    shopIds.forEach((shopId) => {
      dispatch(
        rankingAction.getNoteAppRanking({
          token,
          params: {
            shop_id: String(shopId),
            type: 2,
          },
        })
      );
    });
  }, [dispatch, token, shopIds, programRank?.id]);

  const getListNotePositionAppRanking = useCallback(() => {
    if (!shopIds.length) return;

    shopIds.forEach((shopId) => {
      dispatch(
        rankingAction.getNotePositionAppRanking({
          token,
          params: {
            shop_id: String(shopId),
          },
        })
      );
    });
  }, [dispatch, token, shopIds]);

  // handle fullscreen
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (pathname === '/rank' && fullscreenOnReload === 'true') {
      enterFullscreen(el);
    } else {
      exitFullscreen();
    }
  }, [pathname, enterFullscreen, exitFullscreen, fullscreenOnReload]);

  // handle check if today has data
  useEffect(() => {
    const intervalTimeStep = displayData?.secondChange
      ? Number(displayData?.secondChange) * 1000
      : defaultSecondChange;

    const interval = setInterval(() => {
      setCurrentDate((prevDate) => {
        const today = moment().startOf('day');
        const nextDate = moment(prevDate).add(1, 'day');
        return nextDate.isAfter(today) ? moment().startOf('month') : nextDate;
      });
    }, intervalTimeStep);

    return () => clearInterval(interval);
  }, [currentDate, displayData?.secondChange]);

  // Fetch data
  useEffect(() => {
    fetchArrLoop();
  }, [fetchArrLoop]);

  useEffect(() => {
    fetchRankingPublicGroupShop();
  }, [fetchRankingPublicGroupShop]);

  useEffect(() => {
    fetchRankingPublic();
  }, [fetchRankingPublic]);

  // get nearest days have data (by shop)
  useEffect(() => {
    getDateHaveDataRanking();
  }, [getDateHaveDataRanking]);

  // get note by staff name
  useEffect(() => {
    getListNotePositionAppRanking();
  }, [getListNotePositionAppRanking]);

  // get note by position from No1 to No10 in settin
  useEffect(() => {
    getListNoteAppRanking();
  }, [getListNoteAppRanking]);

  useEffect(() => {
    if (token && selectShop?.id) {
      dispatch(
        rankingAction.fetchOrderByRankingInPosRanking({
          token,
          params: { shop_id: selectShop?.id },
        })
      );
    }
  }, [dispatch, selectShop?.id, token]);

  useEffect(() => {
    if (token) {
      dispatch(shopListAction.fetchShopList({ token }));
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (selectShop?.id) {
      dispatch(
        shopSettingActions.getShopSetting({
          token,
          params: { shop_id: selectShop?.id },
        })
      );
    }
  }, [selectShop?.id]);

  useEffect(() => {
    if (token && selectShop?.id) {
      dispatch(
        rankingAction.getProgramRank({
          token,
          params: { shop_id: selectShop.id },
        })
      );
    }
  }, [dispatch, selectShop?.id, token]);

  useEffect(() => {
    // check get first program that active
    if (token && selectShop?.id && programRank.id) {
      // after that, get detail ranking of that program
      dispatch(
        rankingAction.getOrderByImagesSLiderRanking({
          token,
          params: {
            shop_id: selectShop.id,
            program_rank_id: programRank?.id,
          },
        })
      );
    }
  }, [dispatch, selectShop?.id, token, programRank.length]);

  // process & handle display ranking data
  useEffect(() => {
    if (arrLoop.length > 0) return;
    const localDateRank = JSON.parse(localStorage.getItem('dataDateRankNearest') || '[]');
    const localMonthRank = JSON.parse(localStorage.getItem('dataMonthRank') || '[]');
    const localGroupShopRanking = JSON.parse(localStorage.getItem('dataDateRankGroupShop') || '[]');
    const localGroupShopMonthRanking = JSON.parse(
      localStorage.getItem('dataMonthRankGroupShop') || '[]'
    );

    const groupShopDatePublic =
      Array.isArray(localGroupShopRanking) && localGroupShopRanking.length
        ? localGroupShopRanking
        : rankingGroupShopPublic;
    const dateSource =
      Array.isArray(localDateRank) && localDateRank.length ? localDateRank : rankingNearestPublic;
    const monthSource =
      Array.isArray(localMonthRank) && localMonthRank.length ? localMonthRank : rankingMonthPublic;
    const groupShopMonthPublic =
      Array.isArray(localMonthRank) && localMonthRank.length
        ? localGroupShopMonthRanking
        : rankingGroupShopMonthPublic;

    const updatedArr = transformedArrSorted
      .map((item: any) => {
        let sortedData = [];

        if (item.name?.includes('DAY')) {
          if (item.shop_id) {
            sortedData = [...dateSource].sort((a, b) => {
              return moment(a?.date, 'MM月DD日').diff(moment(b?.date, 'MM月DD日'));
            });
          }
          if (isMaster) {
            if (item.order_by_ranking_id) {
              if (item?.ranking_list_shop?.length > 0) {
                sortedData = [...groupShopDatePublic].sort((a, b) => {
                  return moment(a?.date, 'MM月DD日').diff(moment(b?.date, 'MM月DD日'));
                });
              } else if (item?.select_shop_id) {
                sortedData = [...dateSource].sort((a, b) => {
                  return moment(a?.date, 'MM月DD日').diff(moment(b?.date, 'MM月DD日'));
                });
              }
            }
          } else {
            if (item.order_by_image_slider_ranking_id) {
              if (item?.order_by_images_slider_ranking?.ranking_list_shop?.length > 0) {
                sortedData = [...groupShopDatePublic].sort((a, b) => {
                  return moment(a?.date, 'MM月DD日').diff(moment(b?.date, 'MM月DD日'));
                });
              } else if (item?.order_by_images_slider_ranking?.select_shop_id) {
                sortedData = [...dateSource].sort((a, b) => {
                  return moment(a?.date, 'MM月DD日').diff(moment(b?.date, 'MM月DD日'));
                });
              }
            }
          }
        } else if (item.name?.includes('MONTH')) {
          if (item.shop_id) {
            sortedData = Array.isArray(monthSource) ? [...monthSource] : [];
          }
          if (isMaster) {
            if (item.order_by_ranking_id) {
              if (item?.ranking_list_shop?.length > 0) {
                sortedData = Array.isArray(groupShopMonthPublic) ? [...groupShopMonthPublic] : [];
              } else if (item?.select_shop_id) {
                sortedData = Array.isArray(monthSource) ? [...monthSource] : [];
              }
            }
          } else {
            if (item.order_by_image_slider_ranking_id) {
              if (item?.order_by_images_slider_ranking?.ranking_list_shop?.length > 0) {
                sortedData = Array.isArray(groupShopMonthPublic) ? [...groupShopMonthPublic] : [];
              } else if (item?.order_by_images_slider_ranking?.select_shop_id) {
                sortedData = Array.isArray(monthSource) ? [...monthSource] : [];
              }
            }
          }
        } else {
          sortedData = item.data || [];
        }

        // if (sortedData.length > 0 && item.name?.includes('DAY')) {
        if (sortedData.length > 0) {
          sortedData = sortedData.map((entry: any) => {
            const normalizedRelatedItems = Array.isArray(entry.relatedItems)
              ? entry.relatedItems
              : [];
            if (normalizedRelatedItems.length === 0) return [];
            return {
              ...entry,
              relatedItems: [...normalizedRelatedItems].sort((a, b) => {
                if (item.name?.includes('PROFIT')) {
                  return (a.rank || 0) - (b.rank || 0);
                }
                if (item.name?.includes('NOMINATION')) {
                  return (Number(a.appointee_times) || 0) - (Number(b.appointee_times) || 0);
                }
                return 0;
              }),
            };
          });
        }

        return {
          ...item,
          data: sortedData,
        };
      })
      .filter(Boolean);

    setArrNearestLoop((prev) =>
      JSON.stringify(prev) === JSON.stringify(updatedArr) ? prev : updatedArr
    );
  }, [
    rankingNearestPublic.length,
    transformedArrSorted.length,
    rankingMonthPublic.length,
    rankingGroupShopPublic.length,
    rankingGroupShopMonthPublic.length,
    arrLoop.length,
    isMaster,
  ]);

  // Check data if already to display
  useEffect(() => {
    if (!arrLoop.length || arrLoop.length !== orderByImagesSliderRank.length) return;

    processSequentially(arrLoop, currentRankingItemRunning);
  }, [currentRankingItemRunning, arrLoop]);

  // Check data if fully data is not ready, get first day have data (nearest data)
  useEffect(() => {
    if (arrLoop.length > 0 || !arrNearestLoop.length) return;
    if (arrNearestLoop.length !== orderByImagesSliderRank.length) return;
    const isAllDataReady = arrNearestLoop
      .filter((item) => !item.name.includes('IMAGE_SLIDER'))
      .every((item: any) => Array.isArray(item.data) && item.data.length !== 0);
    if (!isAllDataReady) {
      return;
    }

    processSequentiallyNearest(arrNearestLoop, currentRankingItemRunning);
  }, [currentRankingItemRunning, arrNearestLoop, orderByImagesSliderRank, arrLoop]);

  // show imediately, no wait fully data
  // useEffect(() => {
  //   // if (arrLoop.length > 0) return;
  //   // if (arrNearestLoop.length !== orderByImagesSliderRank.length) return;

  //   // const isAllDataReady = arrNearestLoop.every(
  //   //   (item: any) => Array.isArray(item.data) && item.data.length !== 0
  //   // );
  //   // if (!isAllDataReady) {
  //   //   return;
  //   // }

  //   processSequentiallyNearest(arrNearestLoop, currentRankingItemRunning);
  // }, [currentRankingItemRunning, arrNearestLoop, orderByImagesSliderRank, arrLoop]);

  // for debug + testing, do not delete
  console.log('display data: ', displayData);
  console.log('display nearest data: ', displayDayHaveNearestData);

  return (
    <div ref={containerRef}>
      <div className="overflow-hidden relative w-screen h-screen">
        <div
          className="overflow-hidden w-screen h-screen"
          style={{ backgroundImage: `url(${bgImage})` }}
        ></div>
        <div className="flex absolute inset-0 flex-col">
          <div className="group fixed top-0 left-0 right-0 h-[10vh] z-50">
            <div
              className="flex items-center justify-between px-6 h-full bg-[#000000cc] 
               opacity-0 transition-all duration-300 
               group-hover:opacity-100 
               pointer-events-none group-hover:pointer-events-auto"
            >
              <div className="flex gap-2 items-center w-full">
                <button className="mr-6 border-none" onClick={() => setOpenMenu((prev) => !prev)}>
                  <span>{cts.menuicon}</span>
                </button>
              </div>

              {/* check only current screen is display ranking will show title */}
              {isRanking && (
                <>
                  {/* TEXT TITLE (not stardam group) */}
                  {!checkShopStardamGroup && (
                    <span
                      className="text-[50px] notoserif_black bg-clip-text text-transparent whitespace-nowrap"
                      style={{
                        backgroundImage:
                          'linear-gradient(129.77deg, #F9DF7B 6.11%, #E5C35C 14.35%, #F9DF7B 18.28%, #FFF3A6 28.57%, #F9DF7B 37.46%, #ECBF67 48.68%, #E5C35C 55.98%, #F9DF7B 59.45%, #FFF3A6 68.8%, #F9DF7B 79.1%, #E9BB5E 93.13%)',
                      }}
                    >
                      {renderRankingTitleText()}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex justify-center items-center h-full">
            <>
              {!loading && hasDisplayData ? (
                <>
                  {layout === 1 ? (
                    <LayoutSlide
                      currentTypeDisplayOnScreen={currentTypeDisplayOnScreen}
                      setCurrentRankingItemRunning={setCurrentRankingItemRunning}
                      displayData={
                        displayData?.object?.length ? displayData : displayDayHaveNearestData
                      }
                      nameRunning={
                        sourceNameRefactor?.infoItem?.name ||
                        sourceNameRefactor?.infoItem?.order_by_ranking?.name
                      }
                      currentRankingItemRunning={currentRankingItemRunning}
                      setCurrentTypeDisplayOnScreen={setCurrentTypeDisplayOnScreen}
                    />
                  ) : (
                    <LayoutRow
                      currentRankingItemRunning={currentRankingItemRunning}
                      setCurrentTypeDisplayOnScreen={setCurrentTypeDisplayOnScreen}
                      setCurrentRankingItemRunning={setCurrentRankingItemRunning}
                      displayData={
                        displayData?.object?.length ? displayData : displayDayHaveNearestData
                      }
                      nameRunning={
                        sourceNameRefactor?.infoItem?.name ||
                        sourceNameRefactor?.infoItem?.order_by_ranking?.name
                      }
                    />
                  )}
                </>
              ) : (
                <Spin
                  spinning
                  size="large"
                  indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
                />
              )}
            </>
          </div>
        </div>
      </div>
      <NavMenu
        setSelectMenu={setSelectMenu}
        selectMenu={selectMenu}
        setOpenMenu={setOpenMenu}
        openMenu={openMenu}
        containerRef={containerRef}
      />
    </div>
  );
};

export default HomePage;
